/**
 * Helpers de bitácora: agrupado por día y formato de headers.
 *
 * Sin dependencias externas. Trabaja con timestamps en ms.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DAY_NAMES = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
];

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/**
 * Convierte un timestamp en ms al "inicio del día" (00:00 local).
 * Útil como key estable para agrupar.
 */
export const startOfDayMs = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Etiqueta humana para el header de un grupo de logs.
 *  - 0 días: "Hoy"
 *  - 1 día: "Ayer"
 *  - 2-6 días: nombre del día capitalizado ("Miércoles")
 *  - 7+ días: "16 de mayo" o "16 de mayo de 2024" si es de otro año
 */
export const formatDayHeader = (dayStartMs, nowMs = Date.now()) => {
  if (dayStartMs == null) return '';
  const today = startOfDayMs(nowMs);
  const diffDays = Math.round((today - dayStartMs) / MS_PER_DAY);

  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';

  const d = new Date(dayStartMs);
  if (diffDays >= 2 && diffDays <= 6) {
    const name = DAY_NAMES[d.getDay()];
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  const day = d.getDate();
  const month = MONTH_NAMES[d.getMonth()];
  const sameYear = d.getFullYear() === new Date(nowMs).getFullYear();
  return sameYear
    ? `${day} de ${month}`
    : `${day} de ${month} de ${d.getFullYear()}`;
};

/**
 * Agrupa logs por día (según timestamp local). Asume logs ordenados desc
 * pero NO depende del orden interno — re-ordena cada grupo desc por las dudas.
 *
 * @param {Array} logs - logs con `timestamp` (Firestore Timestamp o ms)
 * @returns {Array<{ dayStartMs: number, label: string, logs: Array }>}
 */
export const groupLogsByDay = (logs, nowMs = Date.now()) => {
  if (!Array.isArray(logs) || logs.length === 0) return [];

  const byDay = new Map();
  for (const log of logs) {
    const ts = log.timestamp?.toMillis?.() ?? log.timestamp ?? null;
    if (ts == null) continue; // log sin timestamp resuelto (ej. serverTimestamp pending)
    const dayKey = startOfDayMs(ts);
    if (!byDay.has(dayKey)) byDay.set(dayKey, []);
    byDay.get(dayKey).push({ log, ts });
  }

  // Ordenar días desc y logs dentro de cada día desc.
  const days = [...byDay.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([dayStartMs, items]) => ({
      dayStartMs,
      label: formatDayHeader(dayStartMs, nowMs),
      logs: items.sort((a, b) => b.ts - a.ts).map((i) => i.log),
    }));

  return days;
};
